// Pure, testable helpers for the WYSIWYG form builder (Onda 3b). No DOM: the
// admin (js/admin/96-forms.js, a classic script) reaches these via
// window.Ordinex.forms.builder; Vitest imports them directly.

// Composite widgets render inside the field app only (their body lives in
// js/app.js); in the admin canvas they show as a labelled placeholder.
const COMPOSITE_TYPES = new Set([
  'tailgate-items', 'flra-table', 'crew-signin', 'signature', 'gc-signature',
  'timesheet-table', 'production-table', 'ewo-labour-table', 'ewo-materials-table',
  'weekly-inspection', 'daily-inspection', 'qaqc-table', 'toolbox-hazards',
]);
export function isCompositeType(type) { return COMPOSITE_TYPES.has(type); }
export function isPreviewable(type) { return !COMPOSITE_TYPES.has(type); }

// Move arr[from] → position to. Mutates + returns; out-of-range is a no-op.
export function moveInArray(arr, from, to) {
  if (from === to || from < 0 || from >= arr.length || to < 0 || to >= arr.length) return arr;
  const [item] = arr.splice(from, 1);
  arr.splice(to, 0, item);
  return arr;
}

// Sample values for a live PDF preview — one placeholder per field so the PDF
// isn't empty. Section headers + composite widgets carry no value.
export function sampleData(fields) {
  const out = {};
  (fields || []).forEach(f => {
    if (f.type === 'section' || f.type === 'photo' || isCompositeType(f.type)) return;
    if (f.type === 'checkbox-group') out[f.id] = (f.options && f.options[0]) ? [f.options[0]] : [];
    else if (f.type === 'radio' || f.type === 'select') out[f.id] = (f.options && f.options[0]) || '';
    else if (f.type === 'checklist') {
      out[f.id] = (f.items || []).map(it => ({ item: it, answer: (f.options && f.options[0]) || 'OK' }));
    } else if (f.type === 'table') {
      const row = n => Object.fromEntries((f.columns || []).map(c => [c.key, c.type === 'number' ? n : 'Sample']));
      out[f.id] = [row('2'), row('4')];
    }
    else if (f.type === 'number') out[f.id] = '5';
    else if (f.type === 'date') out[f.id] = new Date().toISOString().slice(0, 10);
    else out[f.id] = 'Sample';
  });
  return out;
}

// Slug rule for a form_key from a title (Onda 3e / FB3) — must match the resolver
// so resolveSchema(title) finds the row. Lowercase, non-alphanumerics → "_".
export function slugifyFormKey(title) {
  return String(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

// Conditional logic (Onda 3c): a field with `visibleIf = { field, value }` shows
// only when the referenced field's value matches. Loose match; a checkbox-group
// value (array) matches when it includes the target. No condition ⇒ always visible.
export function isFieldVisible(field, values) {
  const c = field && field.visibleIf;
  if (!c || !c.field) return true;
  const v = values ? values[c.field] : undefined;
  if (Array.isArray(v)) return v.includes(c.value);
  return String(v == null ? '' : v) === String(c.value);
}

// Fractional widths (sheet editor). Widths live in integer SIXTHS of the
// printable row — 2 = 1/3, 3 = 1/2, 4 = 2/3, 6 = full — so packing math never
// meets float epsilon. Only WIDTH_TYPES may shrink; everything else is full.
// Every block an admin can place is width-capable: the PDF draws it inside a
// narrowed content band, so a checklist or a table honours a half-width
// column the same way a text field does. Composites stay full width — they
// own the page (crew sign-in rules, signature blocks, the FLRA task pattern).
export const WIDTH_TYPES = new Set([
  'text', 'number', 'date', 'tel', 'email', 'select',
  'textarea', 'radio', 'checkbox-group', 'checklist', 'table', 'photo', 'section',
]);
export const WIDTH_SIXTHS = { third: 2, half: 3, 'two-thirds': 4 };

export function widthSixths(field) {
  if (!field || !WIDTH_TYPES.has(field.type)) return 6;
  return WIDTH_SIXTHS[field.width] || 6;
}

// Group consecutive fractional fields into printable rows (sum ≤ 6 sixths).
// A full-width field is always its own single-cell row. Shared by the sheet
// renderer and the PDF generator so both surfaces break rows identically.
export function packRows(fields) {
  const rows = [];
  let cur = null, sum = 0;
  (fields || []).forEach((field, idx) => {
    const sx = widthSixths(field);
    if (sx >= 6) {
      if (cur) { rows.push(cur); cur = null; sum = 0; }
      rows.push({ cells: [{ field, idx, sx: 6 }] });
      return;
    }
    if (!cur || sum + sx > 6) {
      if (cur) rows.push(cur);
      cur = { cells: [] }; sum = 0;
    }
    cur.cells.push({ field, idx, sx }); sum += sx;
  });
  if (cur) rows.push(cur);
  return rows;
}

// First free id for a pasted/duplicated field: base, base_2, base_3, …
export function uniqueFieldId(base, have) {
  let id = base, n = 1;
  while (have.has(id)) id = `${base}_${++n}`;
  return id;
}

// Editor undo/redo over opaque snapshots (the editor passes JSON strings).
// push() records the state AFTER each committed operation; the editor pushes
// the initial state on open, so undo() returns the previous snapshot and the
// bottom entry is never popped.
export class UndoStack {
  constructor(limit = 100) { this._past = []; this._future = []; this._limit = limit; }
  push(s) {
    this._past.push(s);
    if (this._past.length > this._limit) this._past.shift();
    this._future = [];
  }
  undo() {
    if (this._past.length < 2) return null;
    this._future.push(this._past.pop());
    return this._past[this._past.length - 1];
  }
  redo() {
    if (!this._future.length) return null;
    const s = this._future.pop();
    this._past.push(s);
    return s;
  }
  canUndo() { return this._past.length >= 2; }
  canRedo() { return this._future.length > 0; }
}

// Apply a property patch to a simple field, normalising like the old field modal.
const CHOICE_TYPES = new Set(['radio', 'checkbox-group', 'select']);
const _splitLines = t => String(t).split('\n').map(s => s.trim()).filter(Boolean);

export function applyFieldProps(field, patch) {
  if (patch.label != null) field.label = String(patch.label).trim();
  if (patch.help != null) { const h = String(patch.help).trim(); if (h) field.help = h; else delete field.help; }
  if (patch.placeholder != null) { const p = String(patch.placeholder).trim(); if (p) field.placeholder = p; else delete field.placeholder; }
  if (patch.required != null && field.type !== 'section') field.required = !!patch.required;
  if (patch.optionsText != null && (CHOICE_TYPES.has(field.type) || field.type === 'checklist')) {
    const opts = _splitLines(patch.optionsText);
    field.options = field.type === 'checklist' ? opts.slice(0, 5) : opts;
  }
  // Onda 1 pieces:
  if (patch.itemsText != null && field.type === 'checklist') {
    field.items = _splitLines(patch.itemsText).slice(0, 60);
  }
  if (patch.columnsText != null && field.type === 'table') {
    // One column per line; a leading '#' marks a number column. Keys are
    // re-derived from labels (renaming a column re-keys it — custom-form data
    // isn't queried per-column today, so continuity is not load-bearing).
    const seen = new Set();
    field.columns = _splitLines(patch.columnsText).slice(0, 5).map(line => {
      const isNum = line.startsWith('#');
      const label = (isNum ? line.slice(1) : line).trim();
      let key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'col';
      if (seen.has(key)) { let n = 2; while (seen.has(`${key}_${n}`)) n++; key = `${key}_${n}`; }
      seen.add(key);
      return { key, label, type: isNum ? 'number' : 'text' };
    }).filter(c => c.label);
  }
  if (patch.maxRows != null && field.type === 'table') {
    const mr = parseInt(patch.maxRows, 10);
    if (Number.isFinite(mr)) field.maxRows = Math.min(Math.max(mr, 1), 100);
    else delete field.maxRows;
  }
  if (patch.half != null) {
    if (patch.half && WIDTH_TYPES.has(field.type)) field.width = 'half';
    else delete field.width;
  }
  if ('width' in patch) {
    const v = String(patch.width || '');
    if (v && WIDTH_SIXTHS[v] && WIDTH_TYPES.has(field.type)) field.width = v;
    else delete field.width;
  }
  if ('spaceBefore' in patch) {
    const n = Number(patch.spaceBefore);
    if (Number.isFinite(n) && n > 0) field.spaceBefore = Math.min(120, Math.max(2, Math.round(n)));
    else delete field.spaceBefore;
  }
  if ('minH' in patch) {
    const n = Number(patch.minH);
    if (Number.isFinite(n)) field.minH = Math.min(120, Math.max(8, Math.round(n)));
    else delete field.minH;
  }
  return field;
}
